import { useRef, useState } from "react";
import { Chip } from "@mui/material";
import BaseModal from "../../../components/ui/BaseModal";
import BaseCard from "../../../components/ui/BaseCard";
import BaseButton from "../../../components/ui/BaseButton";
import BaseEditor from "../../../components/ui/BaseEditor";
import { useSocket } from "../../../context/SocketContext";
import useAuth from "../../../hooks/useAuth";
import { fetchOrCreateGroupChatRoom } from "../../../services/api/message";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";

const BulkMessageModal = ({ open, onClose, selectedOffers, workOrderDetail }) => {
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const editorRef = useRef(null);
  const socket = useSocket();
  const { auth } = useAuth();
  const dispatch = useDispatch();

  const cleanHTML = (html = "") => {
    const div = document.createElement("div");
    div.innerHTML = html;
    div.querySelectorAll("p").forEach((p) => {
      if (!p.textContent.trim() && p.innerHTML.includes("br")) p.remove();
    });
    return div.innerHTML;
  };

  const plainText = (html = "") => html.replace(/<[^>]+>/g, "").trim();

  const getEngineerName = (offer) => {
    const name = `${offer?.user?.firstName || ""} ${offer?.user?.lastName || ""}`.trim();
    return name || `Engineer #${offer?.user?.id}`;
  };

  const getMessageTarget = (offer) => {
    // If the owning company is TechDomain's own company, treat as a direct engineer
    const isTechDomainCompany =
      offer?.createdBy?.ownedCompany?.id === Number(process.env.REACT_APP_TECHDOMAIN_COMPANY_ID);
    if (isTechDomainCompany) return offer.user.id;

    // For genuine freelance-company-referred offers, message the company owner
    return offer?.referredEngineerId ? offer.createdBy.id : offer.user.id;
  };

  const handleSend = async () => {
    if (!plainText(message) || !selectedOffers.length || !socket) return;
    setIsSending(true);

    // Deduplicate: multiple engineers from the same company share one chat room
    const seen = new Set();
    const dedupedOffers = selectedOffers.filter((offer) => {
      const target = getMessageTarget(offer);
      if (seen.has(target)) return false;
      seen.add(target);
      return true;
    });

    let succeeded = 0;
    let failed = 0;

    // Process sequentially — concurrent socket + DB calls can exhaust the connection pool
    for (const offer of dedupedOffers) {
      try {
        const engineerId = getMessageTarget(offer);

        // Fetch existing room or create a new one for this engineer
        const roomResponse = await fetchOrCreateGroupChatRoom({
          workOrderId: workOrderDetail.id,
          companyId: workOrderDetail.companyId,
          engineerId,
        });
        const conversationId = roomResponse?.data?.id;
        if (!conversationId) throw new Error("No chat room returned");

        socket.emit("sendMessage", {
          conversationId,
          senderId: auth.user.id,
          message: cleanHTML(message),
          workOrderId: workOrderDetail.id,
          engineerId,
          digitalAssetIds: [],
        });

        succeeded++;
      } catch (err) {
        console.error("Bulk message failed for offer", offer?.id, err);
        failed++;
      }
    }

    if (succeeded > 0) {
      dispatch(
        showToast({
          message: `Message sent to ${succeeded} engineer${succeeded !== 1 ? "s" : ""}`,
          type: "success",
        }),
      );
    }
    if (failed > 0) {
      dispatch(
        showToast({
          message: `Failed to send to ${failed} engineer${failed !== 1 ? "s" : ""} — check console`,
          type: "error",
        }),
      );
    }

    setIsSending(false);
    if (succeeded > 0) onClose();
  };

  return (
    <BaseModal open={open} close={onClose}>
      <div className="bg-[#F6F6F6] w-[520px]">
        <div className="container py-4">
          <h6 className="text-dark border-bottom pb-1">Message Selected Engineers</h6>

          <BaseCard elevation={0} className="mt-4 px-5 py-4">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <p className="text-sm text-[#70757c]">To:</p>
              {selectedOffers.map((offer) => {
                const isTechDomainCompany =
                  offer?.createdBy?.ownedCompany?.id ===
                  Number(process.env.REACT_APP_TECHDOMAIN_COMPANY_ID);
                const isReferred = !!offer?.referredEngineerId && !isTechDomainCompany;
                const label = isReferred
                  ? `${getEngineerName(offer)} (via ${offer.createdBy.ownedCompany?.name})`
                  : getEngineerName(offer);
                return (
                  <Chip
                    key={offer.id}
                    label={label}
                    size="small"
                    sx={{ backgroundColor: "#EFF4FF", color: "#2561A9", fontWeight: 500 }}
                  />
                );
              })}
            </div>
            <div className="border rounded overflow-hidden bg-white">
              <BaseEditor
                ref={editorRef}
                className="w-full text-[13px] px-4 py-3 resize-none overflow-y-auto focus:outline-none min-h-[160px] max-h-[300px] border-none"
                value={message}
                showToolbar
                placeholder="Type your message here..."
                onChange={(body) => setMessage(body ?? "")}
              />
            </div>
          </BaseCard>
        </div>

        <div
          className="flex items-center justify-center bg-white py-5 mt-2"
          style={{ boxShadow: "5px 0px 14px 5px rgba(0, 0, 0, 0.07)" }}
        >
          <BaseButton
            className="py-[5px] px-2 bg-[#E8E8E8] text-dark"
            fullWidth={false}
            onClick={onClose}
          >
            Cancel
          </BaseButton>
          <BaseButton
            isLoading={isSending}
            loaderSize={18}
            disabled={!plainText(message)}
            className="py-[5px] px-4 ml-4 min-w-[120px]"
            color="primary"
            fullWidth={false}
            onClick={handleSend}
          >
            Send Message
          </BaseButton>
        </div>
      </div>
    </BaseModal>
  );
};

export default BulkMessageModal;
